import { useEffect, useState } from 'react';
import { FiX } from 'react-icons/fi';

import { isTargetable } from '@/admin/admin.features';
import { useVhAdminStore } from '@/admin/admin.store';
import type { VhAdminFeature } from '@/admin/admin.types';
import { AdminBadge } from '@/admin/components/AdminBadge';
import { AdminCard, AdminCardList } from '@/admin/components/AdminCard';
import { AdminSelect } from '@/admin/components/AdminSelect';
import { AdminSpinner } from '@/admin/components/AdminSpinner';
import {
  AdminTable,
  AdminTableCell,
  AdminTableHead,
  AdminTableHeadCell,
  AdminTableRow,
} from '@/admin/components/AdminTable';
import { AdminToggle } from '@/admin/components/AdminToggle';
import styles from '@/admin/pages/AllFeaturesPage.module.scss';
import { toast } from '@/utils/toast';

/**
 * The targeting rules for one flag: which named audiences switch this feature on.
 * The wire contract is the other way round — an audience carries its feature list — so
 * adding or dropping an audience here rewrites that audience's list.
 */
const FeatureTargeting = ({ feature }: { feature: VhAdminFeature }) => {
  const audiences = useVhAdminStore((state) => state.audiences);
  const setAudienceTargetsFeature = useVhAdminStore((state) => state.setAudienceTargetsFeature);
  const [saving, setSaving] = useState(false);

  const targeting = audiences.filter((audience) => audience.features.includes(feature.name));
  const available = audiences.filter(
    (audience) => audience.editable && !audience.features.includes(feature.name),
  );

  const retarget = async (audienceId: string, targeted: boolean) => {
    setSaving(true);
    try {
      await setAudienceTargetsFeature(audienceId, feature.name, targeted);
    } catch {
      toast.error("Couldn't update the targeting rules. Try again");
    } finally {
      setSaving(false);
    }
  };

  // The backend only knows a frontend feature while it is beta, so there is nothing to target yet.
  if (!isTargetable(feature)) {
    return <span className={styles.muted}>Mark as beta to target audiences</span>;
  }

  return (
    <div className={styles.targeting}>
      {targeting.length === 0 && <span className={styles.muted}>No audiences</span>}

      {targeting.map((audience) =>
        // The built-in "All features" covers whatever is beta right now — nothing to edit.
        audience.editable ? (
          <span key={audience.id} className={styles.chip}>
            {audience.name}
            <button
              type="button"
              className={styles.chipRemove}
              disabled={saving}
              aria-label={`Stop targeting ${audience.name} with ${feature.name}`}
              onClick={() => retarget(audience.id, false)}
            >
              <FiX />
            </button>
          </span>
        ) : (
          <AdminBadge key={audience.id}>{audience.name}</AdminBadge>
        ),
      )}

      {available.length > 0 && (
        <AdminSelect
          value=""
          label={`Add an audience to ${feature.name}`}
          onChange={(audienceId) => audienceId && retarget(audienceId, true)}
        >
          <option value="">Add audience…</option>
          {available.map((audience) => (
            <option key={audience.id} value={audience.id}>
              {audience.name}
            </option>
          ))}
        </AdminSelect>
      )}
    </div>
  );
};

const FeatureTypeBadge = ({ feature }: { feature: VhAdminFeature }) => (
  <AdminBadge>{feature.type === 'frontend' ? 'Frontend' : 'Backend'}</AdminBadge>
);

export const AllFeaturesPage = () => {
  const { features, featuresLoading, fetchFeatures, setFeatureBeta, fetchAudiences } =
    useVhAdminStore();

  useEffect(() => {
    fetchFeatures();
    fetchAudiences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleBeta = async (name: string, beta: boolean) => {
    try {
      await setFeatureBeta(name, beta);
    } catch {
      toast.error("Couldn't change the beta flag. Try again");
    }
  };

  return (
    <div>
      <div className={styles.header}>
        <h1 className={styles.title}>All Features</h1>
      </div>

      {featuresLoading && features.length === 0 ? (
        <div className={styles.loading}>
          <AdminSpinner />
        </div>
      ) : features.length === 0 ? (
        <div className={styles.empty}>No features found</div>
      ) : (
        <>
          <div className={styles.desktopOnly}>
            <AdminTable>
              <AdminTableHead>
                <AdminTableHeadCell>Feature</AdminTableHeadCell>
                <AdminTableHeadCell>Type</AdminTableHeadCell>
                <AdminTableHeadCell>Status</AdminTableHeadCell>
                <AdminTableHeadCell>Beta</AdminTableHeadCell>
                <AdminTableHeadCell>Audiences</AdminTableHeadCell>
              </AdminTableHead>
              <tbody>
                {features.map((feature) => (
                  <AdminTableRow key={feature.name}>
                    <AdminTableCell>
                      <span className={styles.name}>{feature.name}</span>
                    </AdminTableCell>
                    <AdminTableCell>
                      <FeatureTypeBadge feature={feature} />
                    </AdminTableCell>
                    <AdminTableCell>
                      <AdminBadge tone={feature.beta ? 'success' : 'neutral'}>
                        {feature.beta ? 'Beta' : 'Stable'}
                      </AdminBadge>
                    </AdminTableCell>
                    <AdminTableCell>
                      <AdminToggle
                        checked={feature.beta}
                        onChange={(beta) => toggleBeta(feature.name, beta)}
                        label={`Beta flag for ${feature.name}`}
                      />
                    </AdminTableCell>
                    <AdminTableCell>
                      <FeatureTargeting feature={feature} />
                    </AdminTableCell>
                  </AdminTableRow>
                ))}
              </tbody>
            </AdminTable>
          </div>

          <div className={styles.mobileOnly}>
            <AdminCardList>
              {features.map((feature) => (
                <AdminCard key={feature.name}>
                  <div className={styles.cardHeader}>
                    <span className={styles.name}>{feature.name}</span>
                    <AdminToggle
                      checked={feature.beta}
                      onChange={(beta) => toggleBeta(feature.name, beta)}
                      label={`Beta flag for ${feature.name}`}
                    />
                  </div>
                  <div className={styles.cardBadges}>
                    <FeatureTypeBadge feature={feature} />
                    <AdminBadge tone={feature.beta ? 'success' : 'neutral'}>
                      {feature.beta ? 'Beta' : 'Stable'}
                    </AdminBadge>
                  </div>
                  <div className={styles.cardTargeting}>
                    <FeatureTargeting feature={feature} />
                  </div>
                </AdminCard>
              ))}
            </AdminCardList>
          </div>
        </>
      )}
    </div>
  );
};
